import { computed, onMounted, onUnmounted } from "vue";
import { throttle } from "lodash-es";
import { useScrollStore } from "@/stores/scroll";

export interface ScrollOptions {
  threshold?: number;
  topThreshold?: number;
  throttleMs?: number;
}

const DEFAULT_OPTIONS: Required<ScrollOptions> = {
  threshold: 30,
  topThreshold: 30,
  throttleMs: 250,
};

export function useScroll(options: ScrollOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const scrollStore = useScrollStore();

  let lastScrollTop = 0;
  let ticking = false;
  let accumulatedDelta = 0;

  const isTop = (scrollTop: number) => scrollTop <= opts.topThreshold;
  const isBottom = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return Math.ceil(scrollTop + windowHeight) >= documentHeight;
  };

  const handleScroll = throttle(() => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const delta = scrollTop - lastScrollTop;

        accumulatedDelta += delta;

        if (Math.abs(accumulatedDelta) >= opts.threshold) {
          scrollStore.setDirection(accumulatedDelta > 0 ? "down" : "up");
          accumulatedDelta = 0;
        }

        scrollStore.setScrollTop(scrollTop);
        scrollStore.setIsTop(isTop(scrollTop));
        scrollStore.setIsBottom(isBottom());

        lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
        ticking = false;
      });

      ticking = true;
    }
  }, opts.throttleMs);

  onMounted(() => {
    window.addEventListener("scroll", handleScroll);
  });

  onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
  });

  return computed(() => ({
    isUp: scrollStore.isUp,
    isDown: scrollStore.isDown,
    isAtTop: scrollStore.isAtTop,
    isAtBottom: scrollStore.isAtBottom,
    scrollTop: scrollStore.getScrollTop,
  }));
}
